import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../config/supabase';
import { Calendar, User, Star } from 'lucide-react';
import './WorldsGallery.css';

interface World {
  id: string;
  name: string;
  owner_id: string;
  settings: {
    is_public: boolean;
    max_users: number;
  };
  created_at: string;
  updated_at: string;
  thumbnail_url?: string;
  profiles?: {
    username: string;
  };
}

// Landing-page gallery: only the admin-curated Featured Worlds (featured_worlds
// table), most recently updated first. Renders nothing at all until something is featured,
// so the page has no empty section or stray divider.
const WorldsGallery: React.FC = () => {
  const [featured, setFeatured] = useState<World[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    supabase
      .from('featured_worlds')
      .select(`
        rank,
        world:worlds (
          *,
          profiles!worlds_owner_id_fkey (username)
        )
      `)
      .order('rank', { ascending: true })
      .then(({ data, error }) => {
        if (error || !data) return;
        setFeatured(
          (data as any[])
            .map((row) => row.world as World | null)
            .filter((w): w is World => !!w && !!w.settings?.is_public)
            // Most recently updated first (rank only decides which are featured).
            .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
        );
      });
  }, []);

  // Public gallery → play/view mode, never the builder.
  const enterWorld = (world: { id: string; name: string }) => {
    navigate(`/vr/${world.id}?name=${encodeURIComponent(world.name)}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  if (featured.length === 0) return null;

  return (
    <motion.section
      id="worlds"
      className="landing-gallery"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7 }}
    >
      <div className="worlds-gallery">
        <div className="gallery-header">
          <h2>Featured Worlds</h2>
        </div>
        <div className="worlds-grid">
          {featured.map((world) => (
            <div
              key={world.id}
              className="world-card featured"
              onClick={() => enterWorld(world)}
            >
              <div className="world-thumbnail">
                <span className="featured-badge"><Star size={12} /> Featured</span>
                {world.thumbnail_url ? (
                  <img src={world.thumbnail_url} alt={world.name} />
                ) : (
                  <img src="/gather.webp" alt={world.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.7 }} />
                )}
                <div className="world-overlay">
                  <button className="enter-btn">Enter World</button>
                </div>
              </div>
              <div className="world-info">
                <h3>{world.name}</h3>
                <div className="world-meta">
                  <span className="meta-item">
                    <User size={12} />
                    {world.profiles?.username || 'Anonymous'}
                  </span>
                  <span className="meta-item">
                    <Calendar size={12} />
                    {formatDate(world.updated_at || world.created_at)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

export default WorldsGallery;
